import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
    DIFFERENT_LANGUAGE_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL,
    DIFFERENT_LANGUAGE_PRICES_URL,
    DIFFERENT_LANGUAGE_PRIVACY_POLICY_URL,
    DIFFERENT_LANGUAGE_TERMS_AND_CONDITIONS_URL,
    FIRST_ELEMENT_INDEX,
    GERMAN_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL,
    GERMAN_PRICES_URL,
    GERMAN_PRIVACY_POLICY_URL,
    GERMAN_TERMS_AND_CONDITIONS_URL,
    LANGUAGES,
} from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe/provider/organisation.provider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { signup } from '../../actions/auth.actions';
import { localizationKeys } from '../../constants/localizationKeys';
import { SignupHelper } from '../../helpers/signup.helper';
import { IPersonalInformation } from '../../interfaces/personal-information.interface';
import { IRegistration } from '../../interfaces/registration.interface';
import { AuthProvider } from '../../provider/auth.provider';
@UntilDestroy()
@Component({
    selector: 'aumico-users-sign-up-page',
    templateUrl: './sign-up.page.html',
    styleUrls: ['./sign-up.page.scss'],
})
export class SignUpPage implements OnInit {
    isPersonalInformationFormValid = false;
    isRegisterFormValid = false;
    personalInformationFormValue: IPersonalInformation | undefined;
    registrationFormValue: IRegistration | undefined;
    showRegistration = true;
    backendError = false;
    isCreateAccountButtonClicked = false;
    localizations = localizationKeys;
    userEmailAlreadyExist: boolean;
    @Input() supportedLocales: string[] = [];
    @Input() defaultLanguage = LANGUAGES.ENGLISH;
    @Output() signUpSuccess = new EventEmitter<string>();
    @Output() isPersonalInformationStep = new EventEmitter<boolean>();
    @Output() onPrivacyPolicyClick = new EventEmitter<void>();
    referralId: string = '';
    constructor(
        private readonly provider: AuthProvider,
        private readonly actionsListener$: ActionsSubject,
        private readonly domSanitizer: DomSanitizer,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly UserProvider: OrganizationProvider,
        private route: ActivatedRoute,
    ) {
        this.addSvgIcon('aumico-pattern-icon-loading');
    }

    ngOnInit(): void {
        this.referralId = this.route.snapshot.queryParams['referral-uuid'];

        this.actionsListener$
            .pipe(ofType(signup.success), untilDestroyed(this))
            .subscribe(() => this.onSignUpSuccessfully());

        this.actionsListener$.pipe(ofType(signup.failure), untilDestroyed(this)).subscribe((action) => {
            const error = action.payload;
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            if (error != null && error.status === 409) {
                this.userEmailAlreadyExist = true;
            } else {
                this.onSignUpFail();
            }
        });
    }

    showMetaInformation(): void {
        this.showRegistration = false;
        this.isPersonalInformationStep.emit(!this.showRegistration);
    }

    onPersonalInformationFormStatusChange(personalInformationFormStatus: boolean): void {
        this.isPersonalInformationFormValid = personalInformationFormStatus;
    }

    onPersonalInformationFormValueChange(personalInformationFormValue: IPersonalInformation): void {
        this.personalInformationFormValue = personalInformationFormValue;
    }

    onRegisterFormStatusChange(registerFormStatus: boolean): void {
        this.isRegisterFormValid = registerFormStatus;
    }

    onRegisterFormValueChange(registerFormValue: IRegistration): void {
        this.registrationFormValue = registerFormValue;
    }

    public openPricesLink(): void {
        if (
            this.personalInformationFormValue == null ||
            this.personalInformationFormValue?.locale !== LANGUAGES.SWISS_GERMAN
        ) {
            const locale = this.personalInformationFormValue?.locale.split('-')[FIRST_ELEMENT_INDEX];
            const pricesUrl = this.replaceLocaleInUrl(DIFFERENT_LANGUAGE_PRICES_URL, locale);
            window.open(pricesUrl);
        } else {
            window.open(GERMAN_PRICES_URL);
        }
    }

    public openTermsAndConditionsLink(): void {
        if (
            this.personalInformationFormValue == null ||
            this.personalInformationFormValue?.locale !== LANGUAGES.SWISS_GERMAN
        ) {
            const locale = this.personalInformationFormValue?.locale.split('-')[FIRST_ELEMENT_INDEX];
            const termsAndConditionsUrl = this.replaceLocaleInUrl(DIFFERENT_LANGUAGE_TERMS_AND_CONDITIONS_URL, locale);
            window.open(termsAndConditionsUrl);
        } else {
            window.open(GERMAN_TERMS_AND_CONDITIONS_URL);
        }
    }

    public openPrivacyPolicyLink(): void {
        if (
            this.personalInformationFormValue == null ||
            this.personalInformationFormValue?.locale !== LANGUAGES.SWISS_GERMAN
        ) {
            const locale = this.personalInformationFormValue?.locale.split('-')[FIRST_ELEMENT_INDEX];
            const privacyPolicyUrl = this.replaceLocaleInUrl(DIFFERENT_LANGUAGE_PRIVACY_POLICY_URL, locale);
            window.open(privacyPolicyUrl);
        } else {
            window.open(GERMAN_PRIVACY_POLICY_URL);
        }
    }

    public openAgreementOnOrderDataProcessingLink(): void {
        if (
            this.personalInformationFormValue == null ||
            this.personalInformationFormValue?.locale !== LANGUAGES.SWISS_GERMAN
        ) {
            const locale = this.personalInformationFormValue?.locale.split('-')[FIRST_ELEMENT_INDEX];
            const agreementOnOrderDataProcessingUrl = this.replaceLocaleInUrl(
                DIFFERENT_LANGUAGE_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL,
                locale,
            );
            window.open(agreementOnOrderDataProcessingUrl);
        } else {
            window.open(GERMAN_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL);
        }
    }

    private replaceLocaleInUrl(url: string, locale: string): string {
        return url.replace('{locale}', locale);
    }

    createAccount(): void {
        this.isCreateAccountButtonClicked = true;
        if (this.personalInformationFormValue != null && this.registrationFormValue != null) {
            this.backendError = false;
            this.userEmailAlreadyExist = false;
            const signUpRequest = SignupHelper.prepareSignupRequestData(
                this.personalInformationFormValue,
                this.registrationFormValue,
            );
            this.provider.dispatchSignUp(signUpRequest);
        } else {
            throw new Error('Personal information or register form is not complete!');
        }
    }

    private onSignUpSuccessfully(): void {
        this.acceptReferralIfExist();
        const { email } = this.registrationFormValue;
        this.signUpSuccess.emit(email);
    }

    private acceptReferralIfExist() {
        if (this.referralId) {
            this.UserProvider.dispatchAcceptInviteReferrals(this.referralId.trim());
        }
    }
    private onSignUpFail(): void {
        this.backendError = true;
        this.isCreateAccountButtonClicked = false;
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
